import {
  ColumnDef,
  ColumnFiltersState,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  Table,
  useReactTable,
} from '@tanstack/react-table'
import { observer } from 'mobx-react-lite'
import * as React from 'react'
import { Data, OrgPositionsTableRow } from '../data'
import { fuzzyFilter } from 'app/packs/src/utils/table-helpers'
import { SkeletonCell } from 'components/table/skeleton'
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { store } from 'store/index'

type TableContextProps = {
  columnFilters: ColumnFiltersState
  setColumnFilters: (value: ColumnFiltersState) => void
  data: OrgPositionsTableRow[]
  globalFilter: string
  setGlobalFilter: (value: string) => void
  table: Table<OrgPositionsTableRow>
  loading: boolean
}

const TableContext = React.createContext<TableContextProps | null>(null)

export const TableProvider = observer(
  ({
    loading = false,
    children,
    data,
  }: {
    children: React.ReactNode | ((ctx: TableContextProps) => React.ReactNode)
    loading?: boolean
    data: OrgPositionsTableRow[]
  }) => {
    const [columnFilters, setColumnFilters] =
      React.useState<ColumnFiltersState>([])

    const [globalFilter, setGlobalFilter] = React.useState('')
    const vm = React.useMemo(() => new Data(), [])

    const columns = React.useMemo(() => {
      const cols = vm.columns
      if (!loading) return cols

      return cols.map((col) => {
        if (col.id && ['name'].includes(col.id)) return col
        return {
          ...col,
          cell: SkeletonCell,
        } as ColumnDef<OrgPositionsTableRow, unknown>
      })
    }, [vm, loading])

    const table = useReactTable({
      columns,
      data,
      filterFns: { fuzzy: fuzzyFilter },
      getCoreRowModel: getCoreRowModel(),
      getFilteredRowModel: getFilteredRowModel(),
      getSortedRowModel: getSortedRowModel(),
      onColumnFiltersChange: setColumnFilters,
      onGlobalFilterChange: setGlobalFilter,
      getPaginationRowModel: getPaginationRowModel(),
      initialState: {
        sorting: [{ id: 'name', desc: false }],
        pagination: {
          pageSize: 15,
          pageIndex: 0,
        },
      },
      state: {
        columnFilters,
        globalFilter,
      },
    })

    const contextValue = {
      columnFilters,
      setColumnFilters,
      data,
      globalFilter,
      setGlobalFilter,
      table,
      loading,
    }

    const content =
      children instanceof Function ? children(contextValue) : children

    return (
      <TableContext.Provider value={contextValue}>
        {content}
      </TableContext.Provider>
    )
  }
)

export const useTable = () => {
  const context = React.useContext(TableContext)

  if (context === null) {
    throw new Error('useTable must be used within a TableProvider')
  }

  return context
}
